"use client";

import { type KeyboardEvent, useMemo, useRef, useState } from "react";

import { Swatch } from "~/components/product/variant/Swatch";
import { useVariant } from "~/components/product/variant/useVariant";
import type { SwatchConfig } from "~/config/storefront";
import { cn } from "~/lib/utils";
import type { PdpOption } from "~/server/api/schemas/pdp";

const ARROW_NEXT = new Set(["ArrowRight", "ArrowDown"]);
const ARROW_PREV = new Set(["ArrowLeft", "ArrowUp"]);

/**
 * Colour swatches for a single COLOUR option (P3-05). An ARIA radiogroup:
 * roving tabindex, arrow keys move to the next *available* swatch (wrapping)
 * and select it, Space/Enter selects the focused one. Selection flows through
 * the P3-04 variant engine ({@link useVariant}), which cascades to the gallery,
 * price, and size availability; this component only reads availability and
 * calls `selectOption`. A live region names the hovered/selected colour so the
 * choice is never conveyed by the swatch alone.
 */
export function SwatchGroup({
  option,
  config,
  className,
}: {
  option: PdpOption;
  config: SwatchConfig;
  className?: string;
}) {
  const { selected, availability, selectOption } = useVariant();
  const selectedValueId = selected[option.id] ?? null;
  const [hoveredValueId, setHoveredValueId] = useState<string | null>(null);
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const isAvailable = (valueId: string) =>
    availability[valueId]?.selectable ?? availability[valueId]?.available ?? false;

  // The swatch that owns tabindex 0: the selection, else the first available,
  // else the first value (so the group is always reachable by Tab).
  const activeIndex = useMemo(() => {
    const selectedIndex = option.values.findIndex(
      (value) => value.id === selectedValueId,
    );
    if (selectedIndex !== -1) return selectedIndex;
    const firstAvailable = option.values.findIndex((value) =>
      isAvailable(value.id),
    );
    return firstAvailable === -1 ? 0 : firstAvailable;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option.values, selectedValueId, availability]);

  const focusAndSelect = (index: number) => {
    const value = option.values[index];
    if (!value) return;
    buttonRefs.current[index]?.focus();
    if (isAvailable(value.id)) selectOption(option.id, value.id);
  };

  const moveFocus = (fromIndex: number, step: 1 | -1) => {
    const count = option.values.length;
    for (let hops = 1; hops <= count; hops++) {
      const next = (fromIndex + step * hops + count * hops) % count;
      const candidate = option.values[next];
      if (candidate && isAvailable(candidate.id)) {
        focusAndSelect(next);
        return;
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = buttonRefs.current.indexOf(event.target as HTMLButtonElement);
    if (index === -1) return;
    if (ARROW_NEXT.has(event.key)) {
      event.preventDefault();
      moveFocus(index, 1);
    } else if (ARROW_PREV.has(event.key)) {
      event.preventDefault();
      moveFocus(index, -1);
    } else if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      const value = option.values[index];
      if (value && isAvailable(value.id)) selectOption(option.id, value.id);
    }
  };

  const labelValueId = hoveredValueId ?? selectedValueId;
  const labelName =
    option.values.find((value) => value.id === labelValueId)?.value ?? null;

  return (
    <div className={className} data-testid={`pdp-swatches-${option.id}`}>
      <p className="flex items-baseline gap-2 text-sm font-medium text-fg">
        <span>{option.name}</span>
        {labelName && (
          <span aria-live="polite" className="font-normal text-fg-muted">
            {labelName}
          </span>
        )}
      </p>
      <div
        role="radiogroup"
        aria-label={option.name}
        onKeyDown={onKeyDown}
        onMouseLeave={() => setHoveredValueId(null)}
        className={cn("mt-2 flex flex-wrap gap-1")}
      >
        {option.values.map((value, index) => (
          <Swatch
            key={value.id}
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            value={value}
            selected={value.id === selectedValueId}
            available={Boolean(availability[value.id]?.available || availability[value.id]?.requiresReset)}
            selectable={isAvailable(value.id)}
            selectionHint={availability[value.id]?.requiresReset ? "choose the other options again" : undefined}
            config={config}
            tabIndex={index === activeIndex ? 0 : -1}
            onSelect={() => selectOption(option.id, value.id)}
            onHover={() => setHoveredValueId(value.id)}
          />
        ))}
      </div>
    </div>
  );
}
